import { useCallback, useEffect, useRef, useState } from 'react';
import WebSocketClient from '../services/websocket';
import {
  WebSocketMessage,
  WebSocketUpdateData,
  WebSocketStatusApprovalData,
  WebSocketLockedNodesData,
  WebSocketUserSession,
} from '../types/websocket';
import useClientInfo from './useClientInfo';
import { ActiveUser, GeneralizationSuggestion } from '../types';

interface LockedNode {
  userId: string;
  username: string;
}

interface UseWebSocketProps<T = GeneralizationSuggestion> {
  sessionId: string | number | null;
  userId: string;
  username: string;
  fullName: string;
  onUpdateMessage?: (suggestions: T[]) => void;
  onAcceptOrRejectMessage?: (data: WebSocketStatusApprovalData) => void;
}

export const useWebSocket = <T = GeneralizationSuggestion>({
  sessionId,
  userId,
  username,
  fullName,
  onUpdateMessage,
  onAcceptOrRejectMessage,
}: UseWebSocketProps<T>) => {
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const [lockedNodes, setLockedNodes] = useState<Record<string, LockedNode>>({});
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const { userId: currentUserId, username: currentUserName } = useClientInfo();

  const lockedNodesRef = useRef<Record<string, LockedNode>>({});
  const pendingLocksRef = useRef<Set<string>>(new Set());

  const handleLockedNodesMessage = useCallback((data: WebSocketLockedNodesData) => {
    lockedNodesRef.current = data.data;
    pendingLocksRef.current.clear();
    setLockedNodes(data.data);
  }, []);

  const handleUserSession = useCallback((data: WebSocketUserSession) => {
    setActiveUsers(data.data.clientsArray as ActiveUser[]);
  }, []);

  const handleUpdateMessage = useCallback(
    (data: WebSocketUpdateData) => {
      if (!data.data.suggestions.length) {
        return;
      }

      if (onUpdateMessage) {
        onUpdateMessage(data.data.suggestions as T[]);
      }

      return data.data.suggestions;
    },
    [onUpdateMessage],
  );

  const handleAcceptOrRejectMessage = useCallback(
    (data: WebSocketStatusApprovalData) => {
      if (onAcceptOrRejectMessage) {
        onAcceptOrRejectMessage(data);
      }
    },
    [onAcceptOrRejectMessage],
  );

  const handleWebSocketMessage = useCallback(
    (data: WebSocketMessage) => {
      switch (data.type) {
        case 'LOCKED_NODES':
          handleLockedNodesMessage(data);
          break;
        case 'UPDATE':
          return handleUpdateMessage(data);
        case 'APPROVAL_STATUS':
          handleAcceptOrRejectMessage(data);
          break;
        case 'USER_SESSION':
          handleUserSession(data);
          break;
        default:
          console.log('Unhandled message type:', data.type);
      }
    },
    [handleLockedNodesMessage, handleUpdateMessage, handleAcceptOrRejectMessage, handleUserSession],
  );

  const handleWebSocketMessageRef = useRef(handleWebSocketMessage);
  useEffect(() => {
    handleWebSocketMessageRef.current = handleWebSocketMessage;
  });

  useEffect(() => {
    if (sessionId) {
      const wsClient = new WebSocketClient(
        sessionId.toString(),
        currentUserId.toString(),
        currentUserName.toString(),
        (data) => handleWebSocketMessageRef.current(data),
      );
      wsClientRef.current = wsClient;

      return () => {
        wsClient.close();
        wsClientRef.current = null;
      };
    }
  }, [sessionId, currentUserId, currentUserName]);

  const lockNode = useCallback(
    (nodeId: string) => {
      if (wsClientRef.current && userId && username) {
        if (lockedNodesRef.current[nodeId]?.userId === userId) return;
        if (pendingLocksRef.current.has(nodeId)) return;
        pendingLocksRef.current.add(nodeId);
        wsClientRef.current.lockNode(nodeId, userId, fullName);
      }
    },
    [userId, username, fullName],
  );

  const unlockNode = useCallback(
    (nodeId: string) => {
      if (wsClientRef.current && userId && username) {
        wsClientRef.current.unlockNode(nodeId, userId, fullName);
      }
    },
    [userId, username, fullName],
  );

  const acceptOrRejectNode = useCallback(
    (nodeId: string, accepted: boolean) => {
      if (wsClientRef.current && userId && username) {
        wsClientRef.current.updateSuggestionStatus(nodeId, userId, fullName, accepted);
      }
    },
    [userId, username, fullName],
  );

  const isLockedByCurrentUser = useCallback(
    (nodeId: string) => {
      return lockedNodes[nodeId] && lockedNodes[nodeId].userId === userId;
    },
    [lockedNodes, userId],
  );

  const isLockedByOtherUser = useCallback(
    (nodeId: string) => {
      return lockedNodes[nodeId] && lockedNodes[nodeId].userId !== userId;
    },
    [lockedNodes, userId],
  );

  const getNodeLocker = useCallback(
    (nodeId: string) => {
      return lockedNodes[nodeId];
    },
    [lockedNodes],
  );

  return {
    lockedNodes,
    lockNode,
    unlockNode,
    acceptOrRejectNode,
    isLockedByCurrentUser,
    isLockedByOtherUser,
    getNodeLocker,
    activeUsers,
  };
};
